import _extends from "@babel/runtime/helpers/extends";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _class2, _descriptor, _descriptor2, _AbGenericPicker;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { defaultItemDisplayStringFunction } from "abreact/incubator/ui/form/AbInputUtils";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbList, incrementSelection } from "abreact/input/AbList";
import { AbPlaceholder } from "abreact/input/AbPlaceholder";
import { AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbIterable } from "abweb/collection/AbIterable";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { AbTextInput } from "abreact/input/AbTextInput";
import { AbButton } from "abreact/misc/AbButton";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { CalendarUtils } from "abweb/utils/date/CalendarUtils";
import { KeyboardUtils, KeyValue } from "abweb/utils/KeyboardUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { action, computed, observable, runInAction, makeObservable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
/**
 * A React-based implementation of a month input (with filtering) dropdown component.
 *
 * If user does not pass in their own list of items, the component defaults to the
 * current culture's months.
 */
let AbGenericPicker = observer(_class = (_class2 = (_AbGenericPicker = class AbGenericPicker extends React.Component {
  constructor(abGenericPickerProps) {
    super(abGenericPickerProps);
    _defineProperty(this, "dropdownElement", null);
    _defineProperty(this, "defaultInputID", TypeUtils.generateUniqueId("ab-month-input"));
    _defineProperty(this, "listId", TypeUtils.generateUniqueId("month-list"));
    _initializerDefineProperty(this, "popupShown", _descriptor, this);
    _initializerDefineProperty(this, "filterValue", _descriptor2, this);
    _defineProperty(this, "showPopup", action(() => {
      this.popupShown = true;
      this.filterValue = "";
    }));
    _defineProperty(this, "hidePopup", () => {
      if (this.popupShown) {
        runInAction(() => {
          this.popupShown = false;
          this.filterValue = "";
        });
        this.dropdownElement && FocusUtils.setFocus(this.dropdownElement);
      }
    });
    _defineProperty(this, "onKeyDown", event => {
      if (this.props.disabled !== true) {
        if (event.key === KeyValue.TAB && this.popupShown) {
          this.hidePopup();
        } else if (event.key === KeyValue.ESCAPE) {
          this.hidePopup();
          event.stopPropagation();
          event.preventDefault();
        } else if (event.key === KeyValue.ENTER || event.key === KeyValue.SPACE) {
          if (this.popupShown) {
            KeyboardUtils.simulateKeyDown(event.key, event.currentTarget.ownerDocument.getElementById(this.listId));
            this.hidePopup();
          } else {
            this.showPopup();
          }
          event.stopPropagation();
          event.preventDefault();
        } else if (event.key === KeyValue.ARROW_DOWN || event.key === KeyValue.ARROW_UP) {
          if (this.popupShown) {
            KeyboardUtils.simulateKeyDown(event.key, event.currentTarget.ownerDocument.getElementById(this.listId));
          } else {
            const valueIndex = this.filteredItems.findIndex(item => TypeUtils.equals(this.props.value, AbEnriched.getItem(item), this.props.keyFunction));
            const newSelection = incrementSelection(AbListHelper.from(this.filteredItems), valueIndex === -1 ? undefined : valueIndex, event.key === KeyValue.ARROW_DOWN ? 1 : -1);
            if (newSelection) {
              this.props.onChange(AbEnriched.getItem(newSelection[1]));
            }
          }
        }
      }
      return false;
    });
    _defineProperty(this, "onBackgroundCaptureMouseDown", event => {
      // Only close the popup if the click is in the background, not on the dropdown itself
      if (!this.dropdownElement?.contains(event.target)) {
        this.hidePopup();
      }
    });
    _defineProperty(this, "onButtonClick", e => {
      e.stopPropagation();
      (this.popupShown ? this.hidePopup : this.showPopup)();
    });
    _defineProperty(this, "valueRenderer", item => {
      if (item === undefined) {
        return /*#__PURE__*/React.createElement(AbPlaceholder, {
          placeholder: this.props.placeholder
        });
      }
      return (this.props.itemRenderer ?? defaultItemDisplayStringFunction)(item);
    });
    _defineProperty(this, "renderList", () => {
      if (this.filteredItems.length > 0) {
        // if there are matching items in the filter list, render list as normal
        return /*#__PURE__*/React.createElement(AbList, {
          className: "ab-month-input-list",
          selectionMode: SelectionMode.SINGLE,
          items: this.filteredItems,
          itemRenderer: this.props.itemRenderer,
          value: this.props.value,
          onChange: item => {
            this.props.onChange(item);
            this.hidePopup();
          },
          id: this.listId
        });
      } else {
        // display message if no items match the filter
        return /*#__PURE__*/React.createElement("div", {
          className: "ab-month-input-no-items-message"
        }, AbReactResources.datePicker_monthInput_noItems());
      }
    });
    makeObservable(this);
    this.popupShown = false;
    this.filterValue = "";
  }
  get filteredItems() {
    if (this.filterValue === "") return [...this.props.items];
    return AbIterable.filter(this.props.items, maybeEnrichedItem => {
      const {
        item,
        enrichment
      } = AbEnriched.extract(maybeEnrichedItem);
      if (enrichment?.disabled) return false;
      return this.props.filterFunction(item, this.filterValue, enrichment);
    }).toArray();
  }
  render() {
    const contextSize = this.context;
    const {
      disabled,
      autoFocus,
      ...inputProps
    } = ReactUtils.inputProps("ab-month-input", this.props, [this.props.size ?? contextSize]);
    const inner = /*#__PURE__*/React.createElement(AbWithTooltip, {
      tooltip: this.props.tooltip
    }, /*#__PURE__*/React.createElement("div", _extends({}, inputProps, {
      id: this.props.id ?? this.defaultInputID,
      ref: element => {
        this.dropdownElement = element;
        if (element && autoFocus) {
          FocusUtils.setFocus(element);
        }
      },
      onKeyDown: this.onKeyDown,
      onClick: action(() => !disabled && !this.popupShown && this.showPopup())
    }), !this.popupShown && /*#__PURE__*/React.createElement("div", {
      className: "ab-month-input-value"
    }, this.valueRenderer(this.props.value)), this.popupShown && /*#__PURE__*/React.createElement(AbTextInput, {
      className: "ab-month-input-text-input ab-month-input-text-input-no-border",
      autoFocus: true,
      value: this.filterValue,
      onChange: action(value => {
        this.filterValue = value;
      }),
      disabled: disabled
    }), /*#__PURE__*/React.createElement(AbButton, {
      icon: "absdk:chevron-down",
      className: "ab-no-border-button ab-month-input-chevron-button",
      disabled: disabled,
      onClick: this.onButtonClick
    }), this.popupShown && /*#__PURE__*/React.createElement(AbPopup, {
      flushContent: true,
      onClose: action(() => this.popupShown = false),
      onBackgroundCaptureMouseDown: this.onBackgroundCaptureMouseDown,
      className: "ab-month-input-popup",
      overlay: false,
      positioner: PopupUtils.VERTICAL_POSITIONER,
      origin: this.dropdownElement || undefined
    }, this.renderList())));
    return sizeProviderWrapper(inner, this.props, contextSize);
  }
}, _defineProperty(_AbGenericPicker, "contextType", AbSizeContext), _AbGenericPicker), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "popupShown", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "filterValue", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "filteredItems", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "filteredItems"), _class2.prototype), _class2)) || _class;
export const AbMonthInput = props => {
  const earliestMonth = props.earliestMonth ?? 1;
  const latestMonth = props.latestMonth ?? 12;
  return /*#__PURE__*/React.createElement(AbGenericPicker, _extends({}, props, {
    items: CalendarUtils.months.map((month, index) => {
      const oneIndexedMonth = index + 1;
      return AbEnriched.create(month, {
        disabled: oneIndexedMonth < earliestMonth || oneIndexedMonth > latestMonth
      });
    }),
    filterFunction: (item, filterValue) => item.toLowerCase().indexOf(filterValue.toLowerCase()) === 0
  }));
};
//# sourceMappingURL=AbMonthInput.js.map
